/**
 * useGuests — guests data hook backed by @v0y4ge/api-client.
 * No seed data in the UI. All data comes from the contract layer.
 *
 * Restored 2026-07-03 (Claude Code push-back, drop blocker 1a): re-added the
 * canonical `GuestDto` contract type and the create/update/delete mutation
 * calls this hook had dropped. This was list-only before; screens that let a
 * user edit/remove a guest now actually persist against the real API.
 */
import { useState, useEffect } from "react";
import type { GuestDto } from "@v0y4ge/api-contracts";
import { listGuests, createGuest, updateGuest, deleteGuest } from "@v0y4ge/api-client";
import { onTenantSwitching, subscribeAccess, isSignedIn } from "../../shell/access";

/** Payments (2026-08-04) — per-guest payment summary the Guests list uses for
 * the Paid/Refunded/Pending pill and the Refund/Cancel row actions. Optional;
 * only present once the guest DTO carries a `payment` block (or customFields). */
export interface GuestPayment {
  status?: string;
  amountMinor?: number;
  currency?: string;
  provider?: string;
  refundedMinor?: number;
  paymentId?: string | null;
}

export interface Guest {
  id: string; name: string; email: string; company?: string; ticket?: string;
  /** What a person reads: the ticket's NAME. `ticket` stays the wire value (ticketTier, which a real
   *  registration fills with the tier id) because toInput writes it back. CC, 2026-10-04. */
  ticketLabel?: string;
  status: string; checked?: string; source: string; segments: string[];
  /** When the guest registered (the guest read's `registeredAt`), an ISO instant. */
  registeredAt?: string;
  color?: string; eventId?: string;
  /** The record's own tags, carried so an update (the VIP toggle) can add or remove one without replacing the rest. */
  tags?: string[];
  /** The linked customer record's id, when the guest has one (identity comes from it, never from an email match). */
  customerId?: string;
  firstName?: string; lastName?: string; phone?: string;
  payment?: GuestPayment;
  /** The guest read's customFields, carried through (CC 0036 §1): its `answers` is the plain
   *  raw-answer copy keyed by question id. Labelled answers come from getEventGuestAnswers. */
  customFields?: Record<string, unknown>;
  /** Unsubscribed from marketing (GuestDto `unsubscribed`). */
  unsubscribed?: boolean;
}
/** `error`: the last read was refused or failed (CC, 2026-10-03). `guests` is then [] as before, so a
 *  caller that reads only `guests` is unchanged; a caller that reads `error` can tell a refusal from an
 *  empty list. */
export interface GuestsState { guests: Guest[] | null; total: number; loading: boolean; error?: boolean; }

/** Payments (2026-08-04): surface a guest's payment block wherever it rides —
 * a top-level `payment`, or nested under customFields (snake or camel). */
function guestPayment(g: Record<string, any>): GuestPayment | undefined {
  const p = g.payment ?? g.customFields?.payment ?? g.custom_fields?.payment;
  if (!p) return undefined;
  return {
    status: p.status,
    amountMinor: p.amountMinor ?? p.amount_minor,
    currency: p.currency,
    provider: p.provider,
    refundedMinor: p.refundedMinor ?? p.refunded_minor,
    paymentId: p.paymentId ?? p.payment_id ?? null,
  };
}

export function toGuest(g: Record<string, any>): Guest {
  return {
    id: g.id,
    name: (`${g.firstName ?? g.first_name ?? ""}${g.lastName ?? g.last_name ? " " + (g.lastName ?? g.last_name) : ""}`).trim() || (g.email ?? "(no name)"),
    firstName: g.firstName ?? g.first_name ?? undefined,
    lastName: g.lastName ?? g.last_name ?? undefined,
    email: g.email ?? "",
    phone: g.phone ?? undefined,
    company: g.organization ?? g.company ?? undefined,
    ticket: g.ticketTier ?? g.ticket_tier ?? g.ticketName ?? g.ticket_name ?? undefined,
    ticketLabel: g.ticketName ?? g.ticket_name ?? g.ticketTier ?? g.ticket_tier ?? undefined,
    status: g.status ?? "registered",
    checked: g.checkedInAt ?? g.checked_in_at ?? undefined,
    registeredAt: g.registeredAt ?? g.registered_at ?? undefined,
    source: g.source ?? "web",
    /* Every segment the guest is in: the server sends `segments` (the legacy single segment plus
       each segment the guest was added to by hand). Older rows carry only `segment` (CC, 2026-10-07). */
    segments: Array.isArray(g.segments) ? g.segments.map(String) : (g.segment ? [g.segment] : (Array.isArray(g.tags) ? g.tags : [])),
    eventId: g.eventId ?? g.event_id ?? undefined,
    tags: Array.isArray(g.tags) ? g.tags.map(String) : undefined,
    /* The guest has unsubscribed from marketing (GuestDto `unsubscribed`); exports leave such a guest out (CC, 2026-10-07). */
    unsubscribed: typeof g.unsubscribed === "boolean" ? g.unsubscribed : undefined,
    customerId: g.customerId ?? g.customer_id ?? undefined,
    color: g.color ?? undefined,
    payment: guestPayment(g),
    customFields: (g.customFields ?? g.custom_fields ?? undefined) as Record<string, unknown> | undefined,
  };
}

// GuestDto -> input field mapping for create/update (organization/ticketTier
// are UI-facing aliases of the canonical DTO's own field names).
function toInput(g: Partial<Guest>) {
  return {
    firstName: g.firstName, lastName: g.lastName, email: g.email, phone: g.phone,
    organization: g.company, ticketTier: g.ticket, status: g.status, eventId: g.eventId,
  };
}

/** `enabled: false` holds the read until the caller has what scopes it (CC, 2026-10-03: Event Day
 *  waits for its event, because an unscoped read is refused for an event-scoped role). It is not
 *  sent to the server. Default true: every other caller is unchanged. */
/* Last-good guest reads, per query (2026-10-05). List → profile → back used to drop every
   row and re-read the whole list, so each click blanked the screen until the read returned.
   Now a mount paints from the last read at once and revalidates quietly behind it
   (stale-while-revalidate, the same shape as useEvents). Cleared on a tenant switch. */
const _guestCache = new Map<string, { guests: Guest[]; total: number }>();
onTenantSwitching(() => { _guestCache.clear(); });
/* Signing out drops this session's guest reads (CC, 2026-10-07): a same-workspace sign-in purges
   nothing, so the next person was served the previous person's cached lists. Only this cache: the
   workspace-switch purge would park every store until a tenant change that a sign-in never sends. */
let _sessionEpoch = 0;
if (typeof subscribeAccess === "function") subscribeAccess(() => { if (!isSignedIn()) { _guestCache.clear(); _sessionEpoch++; } });
const guestCache = () => _guestCache;
/** Any cached row by id — lets a profile paint from the list row it was opened from. */
export function cachedGuest(id: string): Guest | undefined {
  for (const v of guestCache().values()) { const g = v.guests.find((x) => x.id === id); if (g) return g; }
  return undefined;
}

export function useGuests(params: { eventId?: string; limit?: number; status?: string; search?: string; enabled?: boolean } = {}) {
  const { enabled = true, ...query } = params;
  const qKey = JSON.stringify(query);
  const [state, setState] = useState<GuestsState>(() => {
    const hit = guestCache().get(qKey);
    return hit ? { guests: hit.guests, total: hit.total, loading: false } : { guests: null, total: 0, loading: true };
  });
  const [nonce, setNonce] = useState(0);
  const key = JSON.stringify({ ...query, nonce, enabled });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // A cached query paints ITS OWN rows at once while it re-reads; one with nothing cached reads as
    // loading (CC, 2026-10-07). It used to keep the previous query's rows with loading false, so going
    // A to B and back to A showed, and exported, B's guests until A's re-read landed.
    const hit = guestCache().get(qKey);
    setState((s) => (hit ? { guests: hit.guests, total: hit.total, loading: false, error: false } : { ...s, loading: true, error: false }));
    const epoch = _sessionEpoch; // a read that lands after a sign-out must not refill the cache
    listGuests({ ...query, status: query.status as any, limit: String(query.limit ?? 200) })
      .then((res: any) => {
        if (cancelled || epoch !== _sessionEpoch) return;
        const raw: any[] = res.guests ?? res.data ?? [];
        const guests = raw.map(toGuest); const total = res.total ?? raw.length;
        guestCache().set(qKey, { guests, total });
        setState({ guests, total, loading: false, error: false });
      })
      .catch(() => { if (!cancelled) setState({ guests: [], total: 0, loading: false, error: true }); });
    return () => { cancelled = true; };
  }, [key]);

  const reload = () => setNonce((n) => n + 1);

  return {
    ...state,
    guests: state.guests ?? [],
    reload,
    /** Create (no id) or update (existing id) a guest via the real API, then reload. */
    upsertGuest: async (g: Partial<Guest> & { id?: string }) => {
      const isNew = !g.id;
      try {
        if (isNew && typeof createGuest === "function") {
          const res: any = await createGuest(toInput(g) as any);
          reload();
          return toGuest(res.guest ?? res);
        }
        if (!isNew && typeof updateGuest === "function") {
          const res: any = await updateGuest(g.id as string, toInput(g) as any);
          reload();
          return toGuest(res.guest ?? res);
        }
      } catch (e) { console.warn("Guest mutation failed:", e); }
      return null;
    },
    removeGuest: async (id: string) => {
      try {
        if (typeof deleteGuest === "function") await deleteGuest(id);
      } catch (e) { console.warn("deleteGuest failed:", e); }
      setState((s) => ({ ...s, guests: (s.guests ?? []).filter((x) => x.id !== id) }));
      for (const [k, v] of guestCache()) guestCache().set(k, { ...v, guests: v.guests.filter((x) => x.id !== id) });
    },
  };
}

export type { GuestDto };
